'use client'

import Link from 'next/link'
import { cn } from '@/utilities/ui'
import { useOffMarketAuth } from '@/components/off-market/OffMarketAuthContext'
import { useOffMarketGate } from '@/components/off-market/OffMarketGate'
import type { ReactNode } from 'react'

interface OffMarketCardWrapperProps {
  href: string
  children: ReactNode
}

export function OffMarketCardWrapper({ href, children }: OffMarketCardWrapperProps) {
  const { isAuthenticated, loading } = useOffMarketAuth()
  const { requestAccess } = useOffMarketGate()

  // When authenticated, drop the blur overlay and link normally. The badge
  // swaps to "View Off-Market Opportunity" only once auth is confirmed (not
  // during the loading race, so signed-out visitors never see a flicker).
  //
  // Every class below MUST be a standalone literal string. This was previously
  // built with a template literal where `[&_.off-market-overlay]:hidden` sat
  // directly against the `${`, so Tailwind's scanner never extracted it as a
  // candidate and the rule was simply absent from the stylesheet — the CTA
  // swapped to "View Off-Market Opportunity" but the photo stayed blurred for
  // signed-in members. Verified: 0 occurrences of `off-market-overlay` in the
  // built CSS before this change, 2 for the CTA classes (Steve 2026-08-24).
  if (loading || isAuthenticated) {
    return (
      <Link
        href={href}
        className={cn(
          'block group',
          '[&_.off-market-overlay]:hidden',
          isAuthenticated && '[&_.off-market-cta-signup]:hidden',
          isAuthenticated && '[&_.off-market-cta-view]:block',
        )}
      >
        {children}
      </Link>
    )
  }

  return (
    <button
      type="button"
      onClick={requestAccess}
      className="flex flex-col justify-start group w-full text-left cursor-pointer align-top"
    >
      {children}
    </button>
  )
}
